import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	ButtonComponent,
	ISelect,
	ITableCellEmitter,
	ITableConfig,
	ITableHeader,
	ITableRow,
	ITableSortEvent,
	IToolbar,
	ListLayoutComponent,
	TableComponent,
	TitleGroupComponent,
	ToolbarComponent,
	TableFilterComponent,
} from '@tecalisdev/infinity';
import { Observable } from 'rxjs';
import { RolePermissionsService } from '../../../core/services/role-permissions.service';
import { Permission } from '../../../core/models/role-permissions.models';
import { environment } from '../../../../environments/environment';
import { buildCurlPostJson } from '../../../panel/utils/build-curl.util';
import { transformTrimObject } from '../../../core/utils/utils';
import { ModalCurlRequestComponent } from '../../../shared/components/modal-curl-request/modal-curl-request.component';
import { PaginatedListController } from '../../../shared/list/paginated-list.controller';
import { MenuItemAction, RowItemAction } from '../../../shared/models/master.models';
import { LoadingService } from '../../../shared/services/loading.service';
import { ModalConfirmationService } from '../../../shared/services/modal-confirmation.service';
import { SnackbarService } from '../../../shared/services/snackbar.service';
import { ToolbarHelperService } from '../../../shared/services/toolbar-helper.service';
import { ModalWorkflowDuplicateComponent } from '../../components/modal-workflow-duplicate/modal-workflow-duplicate.component';
import { ModalWorkflowDuplicateToEnvComponent } from '../../components/modal-workflow-duplicate-to-env/modal-workflow-duplicate-to-env.component';
import { ModalWorkflowFormComponent } from '../../components/modal-workflow-form/modal-workflow-form.component';
import { ModalWorkflowJsonEditorComponent } from '../../components/modal-workflow-json-editor/modal-workflow-json-editor.component';
import { IWorkflowAuthResponse, IWorkflowsListBody, IWorkflowsListResponse } from '../../models/workflows.models';
import { WorkflowsHelperService } from '../../services/workflows-helper.service';
import { WorkflowsService } from '../../services/workflows.service';
import { ModalCreateProcessComponent } from '../../../processes/components/modal-create-process/modal-create-process.component';
import { NavigationService } from '../../../shared/services/navigation.service';

@Component({
	selector: 'app-workflows-list',
	imports: [
		ListLayoutComponent,
		TitleGroupComponent,
		ButtonComponent,
		TranslatePipe,
		ToolbarComponent,
		TableComponent,
		AlertComponent,
		ModalWorkflowFormComponent,
		ModalWorkflowJsonEditorComponent,
		ModalWorkflowDuplicateComponent,
		ModalWorkflowDuplicateToEnvComponent,
		ModalCreateProcessComponent,
		ModalCurlRequestComponent,
		TableFilterComponent,
	],
	templateUrl: './workflows-list.component.html',
	styleUrl: './workflows-list.component.scss',
})
export class WorkflowsListComponent extends PaginatedListController implements OnInit {
	toolbarHelperService = inject(ToolbarHelperService);
	workflowsHelper = inject(WorkflowsHelperService);
	workflowService = inject(WorkflowsService);
	loading = inject(LoadingService);
	router = inject(Router);
	modalConfirmationService = inject(ModalConfirmationService);
	trans = inject(TranslateService);
	snackbarService = inject(SnackbarService);
	location = inject(Location);
	rolePermissions = inject(RolePermissionsService);
	private navigationService = inject(NavigationService);
	private destroyRef = inject(DestroyRef);

	protected readonly Permission = Permission;

	// TABLE
	public toolbar: Signal<IToolbar>;
	public header: ITableHeader[] = this.workflowsHelper.header;
	public override config = this.workflowsHelper.getConfig(
		this.rolePermissions.canViewWorkflowDetail(),
	) as ITableConfig;
	public tableFilters: any;
	private sort: ITableSortEvent = this.workflowsHelper.sort;
	public content: ITableRow[] = [];
	public workflowAuth: IWorkflowAuthResponse;
	public workflow: ISelect;

	public showModalCreate: boolean = false;
	public showModalJsonCreate: boolean = false;
	public showModalDuplicate: boolean = false;
	public workflowToDuplicateToEnv: IWorkflowsListBody | undefined;
	public readonly curlModalOpen = signal(false);

	ngOnInit(): void {
		this.toolbarHelperService.initializeListToolbar(
			this,
			() => this.workflowsHelper.toolbar,
			(saved) => {
				if (saved?.tableFilters && Object.keys(saved.tableFilters).length) {
					this.tableFilters = { ...saved.tableFilters };
				}
				if (saved?.pagination) {
					this.hasPrevPagination = true;
					this.config.pagination = saved.pagination;
				}
			},
		);

		this.trans.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.header = this.workflowsHelper.header;
			this.config = {
				...this.workflowsHelper.getConfig(this.rolePermissions.canViewWorkflowDetail()),
				pagination: this.config.pagination,
			};
		});
	}

	protected override beforeListRequest(): void {
		delete this.config.status;
		this.loading.showTable(this.config, this.header.length);
	}

	protected override afterListRequest(): void {
		this.loading.hideTable(this.config);
	}

	protected createListRequest(): Observable<IWorkflowsListResponse> {
		this.getPayload();
		return this.workflowService.list(this.tableFilters);
	}

	protected applyListResponse(response: IWorkflowsListResponse): void {
		this.content = this.workflowsHelper.transformRow(response.data, {
			canEdit: this.rolePermissions.canEditWorkflow(),
			canDelete: this.rolePermissions.canDeleteWorkflow(),
			canDemo: this.rolePermissions.canLaunchWorkflowDemo(),
			canDuplicateToEnv: this.rolePermissions.canViewWorkflowJson(),
		});
	}

	protected override onListError(_error: HttpErrorResponse): void {
		this.config['status'] = { type: 'error' };
	}

	private getPayload(): void {
		this.tableFilters = this.withPagination(this.tableFilters, {
			sort_by: this.sort.sortKey,
			sort_order: this.sort.sortDirection,
		});
		this.toolbarHelperService.cleanSavedToolbar(this.constructor.name);
	}

	public tableCell(cell: ITableCellEmitter): void {
		const actions = {
			[RowItemAction.DEMO]: () => {
				if (this.rolePermissions.canLaunchWorkflowDemo()) {
					this.workflow = {
						id: cell.rowId.uuid,
						label: cell.rowId.alias,
						value: cell.rowId,
					};
				}
			},
			[RowItemAction.DUPLICATE]: () => {
				if (this.rolePermissions.canViewWorkflowJson()) {
					this.workflowToDuplicateToEnv = cell.rowId as IWorkflowsListBody;
				}
			},
		};

		const actionId = cell?.cellId?.id || cell?.cellId;
		actions[actionId] ? actions[actionId]() : null;
	}

	public tableRow(row: ITableRow & { event?: MouseEvent }): void {
		if (this.rolePermissions.canViewWorkflowDetail()) {
			this.goToDetail(row.clickValue.uuid, row.event);
		}
	}

	private goToDetail(id: string, event?: MouseEvent | null): void {
		const { sort_by, sort_order, ...tableFiltersWithoutSort } = this.tableFilters || {};
		const basePath = '/' + (this.location?.path().split('/').filter(Boolean)[0] ?? 'workflows');
		this.toolbarHelperService.saveToolbarPagination(
			tableFiltersWithoutSort,
			this.toolbar,
			this.config.pagination,
			basePath,
			null,
			this.constructor.name,
		);

		this.navigationService.navigateTo({ routeSegments: ['workflows', 'detail', id] }, event);
	}

	public sortChange(event: ITableSortEvent): void {
		// Si el sortDirection es nullish, significa que se ha desactivado el ordenamiento
		if (!event.sortDirection) this.sort = this.workflowsHelper.resetSort();
		else this.sort = event;

		this.getList();
	}

	public override filterChange(filters: any): void {
		this.tableFilters = transformTrimObject(filters);
		this.setListConfigPagination(
			this.hasPrevPagination
				? { current_page: this.config?.pagination?.page, total: this.config?.pagination?.total }
				: { current_page: 1 },
		);
		this.hasPrevPagination = false;
		this.getList();
	}

	public getWorkflowsListCurl(): string {
		if (!this.tableFilters) return '';
		return buildCurlPostJson(environment.apiUrl, '/profiles', this.tableFilters as object);
	}

	public openCurlModal(): void {
		this.curlModalOpen.set(true);
	}

	public closeCurlModal(): void {
		this.curlModalOpen.set(false);
	}

	public onToolbarAction(action: string | { id?: string }): void {
		const id = typeof action === 'string' ? action : action?.id;
		if (id === MenuItemAction.SHOW_CURL) {
			this.openCurlModal();
		}
	}

	public openModalCreate(event: Event): void {
		if (this.rolePermissions.canViewWorkflowJson()) {
			this.showModalCreate = true;
		} else {
			this.navigationService.navigateTo({ routeSegments: ['workflows', 'create'] }, event);
		}
	}

	public closeModalCreate({
		eventType = undefined,
		event,
	}: {
		eventType: undefined | 'assisted' | 'json' | 'default';
		event?: Event;
	}): void {
		this.showModalCreate = false;
		if (eventType === 'assisted') {
			this.navigationService.navigateTo({ routeSegments: ['workflows', 'create'] }, event);
		} else if (eventType === 'json' && this.rolePermissions.canViewWorkflowJson()) {
			this.showModalJsonCreate = true;
		} else if (eventType === 'default') {
			this.showModalDuplicate = true;
		}
	}

	public closeModalJsonCreate(reload: boolean): void {
		if (reload) this.getList();
		this.showModalJsonCreate = false;
	}

	public closeModalDuplicate(reload: boolean): void {
		if (reload) this.getList();
		this.showModalDuplicate = false;
	}

	public closeModalDuplicateToEnv(reload: boolean): void {
		if (reload) this.getList();
		this.workflowToDuplicateToEnv = undefined;
	}
}
